/**
 * Monday.com Core Infrastructure
 *
 * Shared infrastructure for all Monday.com domain services:
 * - GraphQL query execution
 * - Board/column discovery with fuzzy matching
 * - Column value transformation and parsing
 * - Configuration management
 */

import { mondayClient } from './client';
import type {
  MondayBoard,
  MondayColumn,
  MondayItem,
  MondayColumnValue,
} from '@/types/api/monday.types';
import type { Lead, Deal } from '@/types';
import { matchFields, type TargetColumn } from '@/utils/fuzzyMatch';
import { logger } from '@/utils/logger';

// ============================================================================
// Hub Response Types & Adapters
// ============================================================================

export interface HubColumnValue {
  type: string;
  title?: string | null;
  text?: string | null;
  value?: string | null;
  display_value?: string | null;
  linked_item_ids?: string[] | null;
  settings_str?: string | null;
}

export interface HubBoardItem {
  id: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
  column_values: Record<string, HubColumnValue>;
  group: { id: string; title: string };
}

export interface HubBoard {
  id: string;
  name: string;
  description?: string | null;
  columns: Record<string, HubColumnValue>;
  groups: Array<{ id: string; title: string }>;
}

export interface HubBoardItemsPage {
  data: HubBoardItem[];
  count: number;
  next_cursor: string | null;
}

export interface HubActivityLog {
  id: string;
  type: string;
  entity: string;
  event: string;
  user_id: string;
  account_id: string;
  created_at: string;
  data: string;
}

export interface HubActivityLogsPage {
  data: HubActivityLog[];
  count: number;
  next_cursor: string | null;
}

export interface HubUser {
  id: string;
  name: string;
  email: string;
  title?: string | null;
  enabled: boolean;
  photo_thumb?: string | null;
  photo_small?: string | null;
  photo_original?: string | null;
}

export interface HubUsersPage {
  data: HubUser[];
  count: number;
  next_cursor: string | null;
}

export function hubItemToMondayItem(item: HubBoardItem): MondayItem {
  return {
    id: item.id,
    name: item.name,
    board: { id: '' },
    group: item.group,
    column_values: Object.entries(item.column_values ?? {}).map(([id, cv]) => ({
      id,
      title: cv.title ?? '',
      type: (cv.type ?? 'text') as MondayColumnValue['type'],
      text: cv.text ?? null,
      value: cv.value ?? null,
      display_value: cv.display_value ?? undefined,
      linked_item_ids: cv.linked_item_ids ?? undefined,
    })),
    created_at: item.created_at ?? '',
    updated_at: item.updated_at ?? '',
    creator: { id: '', name: '', email: '' },
  };
}

export function hubBoardToMondayBoard(board: HubBoard): MondayBoard {
  return {
    id: board.id,
    name: board.name,
    board_kind: 'public',
    state: 'active',
    items_count: 0,
    columns: Object.entries(board.columns ?? {}).map(([id, cv]) => ({
      id,
      title: cv.title ?? '',
      type: (cv.type ?? 'text') as MondayColumn['type'],
      settings_str: cv.settings_str ?? '',
    })),
    groups: (board.groups ?? []).map((g) => ({
      id: g.id,
      title: g.title,
      color: '',
      position: '',
    })),
  };
}

// ============================================================================

// ============================================================================
// Column Value Parsers
// ============================================================================

export function parseColumnValues(columnValues: MondayColumnValue[]): Record<string, string | number | null> {
  const parsed: Record<string, string | number | null> = {};

  for (const col of columnValues) {
    if ('display_value' in col && col.display_value) {
      parsed[col.id] = col.display_value as string;
    } else if (col.text) {
      parsed[col.id] = col.text;
    } else if (col.value) {
      try {
        const parsedValue = JSON.parse(col.value);
        if (typeof parsedValue === 'object' && parsedValue !== null) {
          parsed[col.id] = parsedValue.text ?? parsedValue.label ?? null;
        } else {
          parsed[col.id] = parsedValue;
        }
      } catch {
        parsed[col.id] = col.value;
      }
    } else {
      parsed[col.id] = null;
    }
  }

  return parsed;
}

export function getColumnValue(
  values: Record<string, string | number | null>,
  columnMapping: Record<string, string>,
  key: string,
  defaultValue: string | number = ''
): string | number | null {
  const columnId = columnMapping[key];
  if (!columnId) return defaultValue;
  return values[columnId] ?? defaultValue;
}

export function transformItemToLead(item: MondayItem, columnMapping: Record<string, string>): Lead {
  const values = parseColumnValues(item.column_values);
  const companyFromColumn = getColumnValue(values, columnMapping, 'company', '');
  const company = companyFromColumn ? String(companyFromColumn) : item.name;

  return {
    id: item.id,
    company,
    contactName: String(getColumnValue(values, columnMapping, 'contactName', '') || item.name),
    contactTitle: String(getColumnValue(values, columnMapping, 'contactTitle', '')),
    contactEmail: String(getColumnValue(values, columnMapping, 'contactEmail', '')),
    contactPhone: String(getColumnValue(values, columnMapping, 'contactPhone', '')),
    stage: String(getColumnValue(values, columnMapping, 'stage', 'New')) as Lead['stage'],
    value: Number(getColumnValue(values, columnMapping, 'value', 0)) || 0,
    source: String(getColumnValue(values, columnMapping, 'source', '')),
    owner: String(getColumnValue(values, columnMapping, 'owner', 'Unassigned')),
    createdAt: item.created_at?.split('T')[0] ?? '',
    lastActivity: item.updated_at ?? '',
    nextAction: String(getColumnValue(values, columnMapping, 'nextAction', '')),
    nextActionDate: String(getColumnValue(values, columnMapping, 'nextActionDate', '')),
    notes: String(getColumnValue(values, columnMapping, 'notes', '')),
    apolloEnriched: Boolean(getColumnValue(values, columnMapping, 'apolloEnriched', '')),
    apolloData: undefined,
  };
}

export function transformItemToDeal(item: MondayItem, columnMapping: Record<string, string>): Deal {
  const values = parseColumnValues(item.column_values);

  return {
    id: item.id,
    name: item.name,
    company: String(getColumnValue(values, columnMapping, 'company', item.name)),
    stage: String(getColumnValue(values, columnMapping, 'stage', 'New')) as Deal['stage'],
    value: Number(getColumnValue(values, columnMapping, 'value', 0)) || 0,
    probability: Number(getColumnValue(values, columnMapping, 'probability', 0)) || 0,
    closeDate: String(getColumnValue(values, columnMapping, 'closeDate', '')),
    createdAt: item.created_at?.split('T')[0] ?? '',
    updatedAt: item.updated_at ?? '',
    owner: String(getColumnValue(values, columnMapping, 'owner', 'Unassigned')),
    leadId: String(getColumnValue(values, columnMapping, 'leadId', '') || ''),
  };
}

// ============================================================================
// Column Auto-Detection
// ============================================================================

export const DEFAULT_LEAD_COLUMN_MAPPING: Record<string, string> = {
  company: 'connect_boards2',
  contactName: 'contact_name',
  contactTitle: 'contact_title',
  contactEmail: 'email',
  contactPhone: 'phone',
  stage: 'status',
  value: 'numbers',
  source: 'attribution',
  owner: 'person',
  nextAction: 'next_action',
  nextActionDate: 'date',
  notes: 'long_text',
  apolloEnriched: 'checkbox',
};

export const DEFAULT_DEAL_COLUMN_MAPPING: Record<string, string> = {
  company: 'text',
  stage: 'status',
  value: 'numbers',
  probability: 'numbers8',
  closeDate: 'date',
  owner: 'person',
  leadId: 'connect_boards',
};

const VALUE_COLUMN_PATTERNS = ['deal value', 'value', 'amount', 'deal amount', 'price', 'revenue', 'deal size'];
const PROBABILITY_COLUMN_PATTERNS = ['probability', 'chance', 'likelihood', 'win rate', 'confidence'];
const SOURCE_COLUMN_PATTERNS = ['attribution', 'source', 'lead source', 'channel', 'origin'];
const NUMERIC_COLUMN_TYPES = ['numbers', 'formula', 'mirror', 'numeric'];

export function autoDetectLeadColumns(
  columns: Array<{ id: string; title: string; type: string }>
): Partial<Record<string, string>> {
  const detected: Partial<Record<string, string>> = {};

  logger.log('[Monday] Leads board columns:', columns.map(c => `${c.title} (${c.id}, type: ${c.type})`));

  for (const col of columns) {
    const titleLower = col.title.toLowerCase();

    if (!detected.source) {
      if (SOURCE_COLUMN_PATTERNS.some(p => titleLower.includes(p))) {
        detected.source = col.id;
        logger.log(`[Monday] Auto-detected source column: "${col.title}" (${col.id}, type: ${col.type})`);
      }
    }

    if (col.type === 'board_relation' && !detected.company) {
      if (titleLower.includes('account') || titleLower.includes('company')) {
        detected.company = col.id;
        logger.log(`[Monday] Auto-detected company column: "${col.title}" (${col.id})`);
      }
    }

    if (col.type === 'status' && !detected.stage) {
      if (titleLower.includes('stage') || titleLower.includes('status')) {
        detected.stage = col.id;
        logger.log(`[Monday] Auto-detected stage column: "${col.title}" (${col.id})`);
      }
    }

    if (col.type === 'people' && !detected.owner) {
      if (titleLower.includes('owner') || titleLower.includes('assign') || titleLower.includes('rep') || titleLower.includes('seller')) {
        detected.owner = col.id;
        logger.log(`[Monday] Auto-detected owner column: "${col.title}" (${col.id})`);
      }
    }

    if (col.type === 'email' && !detected.contactEmail) {
      detected.contactEmail = col.id;
      logger.log(`[Monday] Auto-detected email column: "${col.title}" (${col.id})`);
    }

    if (col.type === 'phone' && !detected.contactPhone) {
      detected.contactPhone = col.id;
      logger.log(`[Monday] Auto-detected phone column: "${col.title}" (${col.id})`);
    }
  }

  return detected;
}

export function getAvailableColumnIds(
  columns: Array<{ id: string; title: string; type: string }>
): Set<string> {
  return new Set(columns.map((c) => c.id));
}

export function autoDetectDealColumns(
  columns: Array<{ id: string; title: string; type: string }>
): Partial<Record<string, string>> {
  const detected: Partial<Record<string, string>> = {};

  logger.log('[Monday] Deals board columns:', columns.map(c => `${c.title} (${c.id}, type: ${c.type})`));

  for (const col of columns) {
    const titleLower = col.title.toLowerCase();
    const isNumericType = NUMERIC_COLUMN_TYPES.includes(col.type);

    if (isNumericType && !detected.value) {
      if (VALUE_COLUMN_PATTERNS.some(p => titleLower.includes(p))) {
        detected.value = col.id;
        logger.log(`[Monday] Auto-detected value column: "${col.title}" (${col.id}, type: ${col.type})`);
      }
    }

    if (isNumericType && !detected.probability) {
      if (PROBABILITY_COLUMN_PATTERNS.some(p => titleLower.includes(p))) {
        detected.probability = col.id;
        logger.log(`[Monday] Auto-detected probability column: "${col.title}" (${col.id}, type: ${col.type})`);
      }
    }

    if (col.type === 'status' && !detected.stage) {
      if (titleLower.includes('stage') || titleLower.includes('status') || titleLower.includes('phase')) {
        detected.stage = col.id;
        logger.log(`[Monday] Auto-detected stage column: "${col.title}" (${col.id})`);
      }
    }

    if ((col.type === 'text' || col.type === 'board_relation') && !detected.company) {
      if (titleLower.includes('company') || titleLower.includes('account') || titleLower.includes('client')) {
        detected.company = col.id;
        logger.log(`[Monday] Auto-detected company column: "${col.title}" (${col.id})`);
      }
    }

    if (col.type === 'date' && !detected.closeDate) {
      if (titleLower.includes('close') || titleLower.includes('expected') || titleLower.includes('target')) {
        detected.closeDate = col.id;
        logger.log(`[Monday] Auto-detected closeDate column: "${col.title}" (${col.id})`);
      }
    }
  }

  if (!detected.value) {
    const exactMatches = ['deal value', 'value', 'amount'];
    for (const match of exactMatches) {
      const col = columns.find(c => c.title.toLowerCase() === match);
      if (col) {
        detected.value = col.id;
        logger.log(`[Monday] Found exact '${col.title}' column: (${col.id}, type: ${col.type})`);
        break;
      }
    }
  }

  if (!detected.value) {
    const firstNumericCol = columns.find(c => NUMERIC_COLUMN_TYPES.includes(c.type));
    if (firstNumericCol) {
      detected.value = firstNumericCol.id;
      logger.log(`[Monday] Using first numeric column for value: "${firstNumericCol.title}" (${firstNumericCol.id})`);
    }
  }

  return detected;
}

// ============================================================================
// Type Exports
// ============================================================================

export interface MondayServiceConfig {
  leadsBoardId: string;
  accountsBoardId?: string;
  dealsBoardId?: string;
  assetsBoardId?: string;
  contactsBoardId?: string;
  columnMappings?: {
    leads?: Record<string, string>;
    accounts?: Record<string, string>;
    deals?: Record<string, string>;
    assets?: Record<string, string>;
    contacts?: Record<string, string>;
  };
}

export interface MondayUser {
  id: string;
  name: string;
  email: string;
  photo_thumb?: string;
  photo_small?: string;
  photo_original?: string;
  title?: string;
  enabled: boolean;
}

export interface MondayContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
  company: string;
  linkedIn?: string;
  status: string;
  source: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  owner: string;
  location?: string;
  accountId?: string;
  accountName?: string;
  linkedLeadIds?: string[];
  linkedDealIds?: string[];
}

export interface MondayActivityLog {
  id: string;
  event: string;
  data: string;
  created_at: string;
  entity?: string;
  user_id?: string;
  account_id?: string;
}

export interface ParsedActivityData {
  column_id?: string;
  column_type?: string;
  column_title?: string;
  previous_value?: unknown;
  value?: unknown;
  pulse_id?: string;
  pulse_name?: string;
}

export interface StageTransition {
  itemId: string;
  itemName: string;
  fromStage: string | null;
  toStage: string;
  timestamp: string;
  userId?: string;
  userName?: string;
}

// ============================================================================
// Core Infrastructure Class
// ============================================================================

export class MondayCore {
  private config: MondayServiceConfig | null = null;
  private columnMappingsCache: Map<string, Map<string, { id: string; title: string; score: number; matchType: string }>> = new Map();

  readonly excludedStages = new Set([
    'unqualified',
    'disqualified',
    'junk',
    'spam',
    'invalid',
    'not interested',
    'bad fit',
    'no budget',
  ]);

  configure(config: MondayServiceConfig): void {
    this.config = config;
    this.columnMappingsCache.clear();
  }

  getConfig(): MondayServiceConfig {
    if (!this.config) {
      throw new Error('MondayService not configured. Call configure() first.');
    }
    return this.config;
  }

  async discoverColumnMappings(
    boardId: string,
    fieldNames: string[],
    forceRefresh = false
  ): Promise<Map<string, { id: string; title: string; score: number; matchType: string }>> {
    const cacheKey = boardId;

    if (!forceRefresh && this.columnMappingsCache.has(cacheKey)) {
      const cached = this.columnMappingsCache.get(cacheKey)!;
      const allFieldsCached = fieldNames.every((f) => cached.has(f));
      if (allFieldsCached) {
        logger.log(`[Monday] Using cached column mappings for board ${boardId}`);
        return cached;
      }
    }

    logger.log(`[Monday] Discovering column mappings for board ${boardId}...`);

    if (forceRefresh) {
      this.columnMappingsCache.delete(boardId);
      mondayClient.invalidateCache(`monday:.*board.*${boardId}`);
    }

    const board = await this.getBoard(boardId);
    if (!board) {
      logger.simpleWarn(`[Monday] Could not fetch board ${boardId} for column discovery`);
      return new Map();
    }

    const targetColumns: TargetColumn[] = board.columns.map((col) => ({
      id: col.id,
      title: col.title,
    }));

    const columnTypeMap = new Map<string, string>();
    logger.log(`[Monday] All columns on board ${boardId}:`);
    for (const col of board.columns) {
      columnTypeMap.set(col.id, col.type || 'text');
      logger.log(`  - ${col.id}: "${col.title}" (type: ${col.type || 'unknown'})`);
    }

    const matches = matchFields(fieldNames, targetColumns, 0.4);

    const enhancedMatches = new Map<string, { id: string; title: string; score: number; matchType: string; columnType: string }>();
    for (const [field, match] of matches.entries()) {
      const columnType = columnTypeMap.get(match.id) || 'text';
      enhancedMatches.set(field, { ...match, columnType });
    }

    logger.log(`[Monday] Column mapping results for board ${boardId}:`);
    for (const [field, match] of enhancedMatches.entries()) {
      logger.log(`  - "${field}" → "${match.title}" (${match.id}) [score: ${match.score.toFixed(2)}, type: ${match.matchType}, colType: ${match.columnType}]`);
    }

    const unmatchedFields = fieldNames.filter((f) => !enhancedMatches.has(f));
    if (unmatchedFields.length > 0) {
      logger.log(`[Monday] Unmatched fields: ${unmatchedFields.join(', ')}`);
      logger.log(`[Monday] Available columns: ${targetColumns.map((c) => c.title).join(', ')}`);
    }

    const existingCache = this.columnMappingsCache.get(cacheKey) || new Map();
    for (const [field, match] of enhancedMatches.entries()) {
      existingCache.set(field, match);
    }
    this.columnMappingsCache.set(cacheKey, existingCache);

    return existingCache;
  }

  async getColumnId(boardId: string, fieldName: string): Promise<string | null> {
    const mappings = await this.discoverColumnMappings(boardId, [fieldName]);
    return mappings.get(fieldName)?.id ?? null;
  }

  async buildColumnValues(
    boardId: string,
    data: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const fieldNames = Object.keys(data);
    const mappings = await this.discoverColumnMappings(boardId, fieldNames);

    const columnValues: Record<string, unknown> = {};

    for (const [field, value] of Object.entries(data)) {
      const mapping = mappings.get(field);
      if (!mapping) {
        logger.log(`[Monday] No column mapping found for field "${field}", skipping`);
        continue;
      }

      const transformedValue = this.transformValueForMonday(field, value, mapping.title);
      if (transformedValue !== null) {
        columnValues[mapping.id] = transformedValue;
      }
    }

    return columnValues;
  }

  transformValueForMonday(fieldName: string, value: unknown, columnTitle: string): unknown {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const fieldLower = fieldName.toLowerCase();
    const titleLower = columnTitle.toLowerCase();

    if (fieldLower.includes('enriched') || titleLower.includes('enriched') ||
        fieldLower.includes('checkbox') || titleLower.includes('checkbox')) {
      const isChecked = value === true || value === 'true' || value === 1;
      return { checked: isChecked ? 'true' : 'false' };
    }

    if (fieldLower.includes('date') || titleLower.includes('date')) {
      if (value instanceof Date) {
        return { date: value.toISOString().split('T')[0] };
      }
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return { date: value.split('T')[0] };
      }
      const dateObj = new Date(String(value));
      if (!isNaN(dateObj.getTime())) {
        return { date: dateObj.toISOString().split('T')[0] };
      }
      return null;
    }

    if (fieldLower.includes('email') || titleLower.includes('email')) {
      return { email: String(value), text: String(value) };
    }

    if (fieldLower.includes('phone') || titleLower.includes('phone')) {
      const originalPhone = String(value);
      logger.log(`[Monday] Skipping phone field to avoid API errors: "${originalPhone}"`);
      return null;
    }

    if (fieldLower.includes('linkedin') || fieldLower.includes('url') || fieldLower.includes('website')) {
      return { url: String(value), text: String(value) };
    }

    return String(value);
  }

  async getBoard(boardId?: string): Promise<MondayBoard | null> {
    const id = boardId ?? this.getConfig().leadsBoardId;

    const hubBoard = await mondayClient.get<HubBoard>(
      `/hub/board/${id}`,
      { cache: true, cacheTTL: 60 * 60 * 1000 }
    );

    return hubBoard ? hubBoardToMondayBoard(hubBoard) : null;
  }

  async findOrCreateGroup(boardId: string, groupName: string): Promise<string> {
    const board = await this.getBoard(boardId);
    if (board?.groups) {
      const existingGroup = board.groups.find(
        (g) => g.title.toLowerCase() === groupName.toLowerCase()
      );
      if (existingGroup) {
        logger.log(`[Monday] Found existing group "${groupName}": ${existingGroup.id}`);
        return existingGroup.id;
      }
    }

    logger.log(`[Monday] Creating new group "${groupName}" on board ${boardId}`);
    const result = await mondayClient.post<{ id: string; title: string }>(
      '/hub/groups',
      { boardId, groupName },
    );

    mondayClient.invalidateCache(`/hub/board/${boardId}`);

    logger.log(`[Monday] Created group "${groupName}": ${result.id}`);
    return result.id;
  }

  async getColumnMappingsForBoard(boardId: string, fieldNames: string[]): Promise<{
    mappings: Map<string, { id: string; title: string; score: number; matchType: string }>;
    availableColumns: Array<{ id: string; title: string; type: string }>;
  }> {
    const board = await this.getBoard(boardId);
    if (!board) {
      return { mappings: new Map(), availableColumns: [] };
    }

    const mappings = await this.discoverColumnMappings(boardId, fieldNames);

    return {
      mappings,
      availableColumns: board.columns.map((col) => ({
        id: col.id,
        title: col.title,
        type: col.type,
      })),
    };
  }

  saveCustomColumnMappings(boardId: string, mappings: Record<string, string>): void {
    const cached = this.columnMappingsCache.get(boardId) || new Map();

    for (const [field, columnId] of Object.entries(mappings)) {
      cached.set(field, { id: columnId, title: field, score: 1.0, matchType: 'manual' });
    }

    this.columnMappingsCache.set(boardId, cached);
    logger.log(`[Monday] Saved custom column mappings for board ${boardId}:`, mappings);
  }

  clearCache(): void {
    mondayClient.invalidateCache('monday:');
  }

  invalidateLeadsCache(): void {
    mondayClient.invalidateCache('monday:.*leads');
  }

  invalidateAccountsCache(): void {
    mondayClient.invalidateCache('monday:.*accounts');
  }
}

// Re-export types used by domain files
export type { MondayBoard, MondayItem, MondayColumnValue };
export type { Lead, Deal };
export type { ApolloData } from '@/types';
